import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';   
import { reportError } from '../v2/lib/errorReporter';

const axiosConfig: AxiosRequestConfig = {
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3306/api', 
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
}; 

const axiosInstance: AxiosInstance = axios.create(axiosConfig); 

// Flag to prevent multiple simultaneous refresh calls
let isRefreshing = false;
// Queue of requests waiting for token refresh
let failedQueue: Array<{
  resolve: (config: InternalAxiosRequestConfig) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown | null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve({} as InternalAxiosRequestConfig);
    }
  });
  failedQueue = [];
};

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => { 
    return response;
  },
  async (error) => { 
    const originalRequest = error.config;

    // Handle 401: attempt refresh and retry
    // This covers both TOKEN_EXPIRED (JWT expired but cookie still sent)
    // and missing accessToken cookie (browser removed it after maxAge)
    /**
     * Endpoints donde un 401 **no** se debe intentar renovar.
     *
     * Solo `/auth/login`: ahí el 401 significa "credenciales incorrectas", no "token
     * vencido", y pedir un refresh no lo arreglaría — solo agregaría una petición y
     * retrasaría el mensaje de error.
     *
     * 📌 **`/auth/register` se sacó de esta lista a propósito.** Antes estaba porque el
     * endpoint no pedía sesión; ahora exige `US_CREATE`, así que un 401 ahí sí es un
     * token vencido y sí se debe renovar y reintentar. Dejarlo excluido haría que
     * guardar el formulario de un usuario nuevo fallara cada vez que el token venció
     * mientras se llenaba — que son 15 minutos, menos de lo que toma capturar permisos.
     */
    const isAuthEndpoint = originalRequest.url?.includes('/auth/login');
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      if (isRefreshing) {
        // Another refresh is in progress — queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: () => {
              resolve(axiosInstance(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await axios.post(
          `${axiosConfig.baseURL}/auth/refresh`,
          {},
          { withCredentials: true }
        );
        /**
         * 🔑 **Se avisa a la app para que vuelva a leer sus permisos.**
         *
         * ## El problema que resuelve
         *
         * `permissionsActives` se captura al iniciar sesión y solo se vuelve a pedir cuando
         * `AuthContext` se monta, o sea en una **recarga completa**. Navegar entre pantallas
         * no lo remonta, así que en una sesión de todo el día el listado de permisos se
         * queda congelado en el del momento del login.
         *
         * Consecuencia real, ya reportada: a un usuario le dieron `LD_EDIT` con la sesión
         * abierta. El **backend** lo aceptó de inmediato —`authorize()` consulta la base en
         * cada petición, nunca está rancio—, pero el **frontend** siguió escondiendo el
         * selector de estatus porque su copia era anterior al cambio. Se veía como "no
         * puedo cambiar el estatus" y se arregló al volver a entrar.
         *
         * ## Por qué aquí
         *
         * Este es el único punto por el que pasa toda sesión activa de forma automática: el
         * token de acceso vive 15 minutos, así que en cuanto alguien sigue trabajando se
         * renueva. Enganchar el refresco aquí acota lo rancio a **15 minutos** en lugar de a
         * toda la sesión, y no cuesta una petición extra por pantalla: cuesta una cada vez
         * que el token se renovaba de todos modos.
         */
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('dylo:session-refreshed'));
        }
        processQueue(null);
        return axiosInstance(originalRequest);
      } catch (refreshError: any) {
        processQueue(refreshError);
        // Solo si el refresh es rechazado por auth (401/403) consideramos la
        // sesión no recuperable y avisamos a la app para que limpie el estado
        // de auth. Así se evita el bucle de re-fetch / rebote de navegación
        // (dashboard -> login -> dashboard ...). Ante errores de red NO se
        // desloguea (puede ser una caída temporal del servidor).
        const refreshStatus = refreshError?.response?.status;
        if (
          (refreshStatus === 401 || refreshStatus === 403) &&
          typeof window !== 'undefined'
        ) {
          window.dispatchEvent(new CustomEvent('dylo:session-expired'));
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // For other errors, log and reject
    if (error.response) { 
      console.error('Error de respuesta:', error.response.data);
    } else if (error.request) { 
      console.error('Error de solicitud:', error.request);

      /**
       * 🔑 **La petición salió y no hubo respuesta: este es el caso que el servidor NO PUEDE
       * registrar por sí mismo.**
       *
       * Es la razón de ser del reportador. Si hay `error.response`, el backend atendió la
       * petición y ya quedó en sus registros; el que se pierde es este —caída de red, DNS,
       * CORS, timeout, servidor abajo—, porque **nadie del otro lado recibió nada que
       * anotar**. Se guarda en `localStorage` y se manda cuando la red vuelva.
       *
       * ⚠️ Se excluye `/client-errors`: si el propio buzón es el que no responde, reportarlo
       * generaría otro error que también se reporta, y el navegador se quedaría girando
       * mientras la cola crece. Es la regla 3 del reportador.
       *
       * 📌 Solo van metadatos, **nunca el cuerpo** de la petición: la bitácora se lee desde
       * la interfaz y un cuerpo puede traer datos de un cliente o una contraseña mal escrita.
       */
      const url = String(originalRequest?.url || '');
      if (!url.includes('/client-errors')) {
        reportError({
          kind: 'network',
          message: `No response from the API: ${error.message || 'request failed'}`,
          context: {
            method: String(originalRequest?.method || '').toUpperCase() || null,
            endpoint: url.slice(0, 200) || null,
            code: error.code || null,
          },
        });
      }
    } else { 
      console.error('Error:', error.message);
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
